import classNames from "classnames";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

// context
import { ContractContext } from "../../../providers/contract";
import { ROUTES } from "../../../setup/constants";
import { ECONTRACT_STATUS } from "../../../enums";
import {
  EPaymentBrand,
  ESenderRole,
  ETrasactionsType,
  IContractDetailsResponseData,
} from "../../../setup/api/services/contract/models";
import { getMilestoneContracts } from "../../../setup/api/services/contract";
import { getArabicOrdinalFemale } from "../../../setup/util/arabicOrdinalName";

// components
import BackButton from "../../../components/contract/backButton";
import { GET_MILESTONE_STATUSES } from "../../../components/contract/milestones/constants";
import SettlementRequests from "./SettlementRequests";

// styles
import styles from "./styles.module.scss";

/**
 * Details page of a complex contract, mirroring the business portal's complex
 * contract page. It only presents the project and links to its milestones —
 * every admin action lives on the milestone's own page.
 */
const MilestoneContractDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { contract, actions } = useContext(ContractContext);

  const [milestones, setMilestones] = useState<IContractDetailsResponseData[]>(
    []
  );

  const fetchMilestoneDetails = useCallback(
    async (id: string, pageSize?: number) => {
      try {
        const response = await getMilestoneContracts({
          complexParentId: id,
          pageSize,
        });
        setMilestones(response?.data?.content);
      } catch (error) {
        navigate(ROUTES.CONTRACT_NOT_FOUND);
        console.error(error);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (id && contract) fetchMilestoneDetails(id, contract.activeChildrenCount);
  }, [fetchMilestoneDetails, id, contract]);

  const back = () => {
    try {
      if (window?.history?.state) {
        navigate(-1);
      } else navigate(ROUTES.HOME);
    } catch (error) {
      navigate(ROUTES.HOME);
    }
  };

  const totalAmountForComplex = useMemo(
    () =>
      milestones.reduce((acc, milestone) => {
        const sumFor = (role: ESenderRole) =>
          milestone?.parties
            ?.filter((party) => party?.role === role)
            ?.reduce((innerAcc, cur) => innerAcc + +(cur?.amount || 0), 0) || 0;
        return (
          acc +
          Math.max(sumFor(ESenderRole.CUSTOMER), sumFor(ESenderRole.PROVIDER))
        );
      }, 0),
    [milestones]
  );

  const uniquePhoneCount = useMemo(
    () =>
      new Set(
        milestones
          .flatMap((m) => m?.parties || [])
          .map((p) => p?.phoneNumber)
          .filter(Boolean)
      ).size,
    [milestones]
  );

  const complexStats = [
    {
      label: t("lbl_total_project_value"),
      value: `${(
        contract?.activeChildrenTotalAmount || totalAmountForComplex
      )?.toLocaleString()} ${t("lbl_saudi_currency")}`,
      color: "#0D44AD",
    },
    {
      label: t("lbl_total_parties"),
      value: `${contract?.activeChildrenPartiesCount ?? uniquePhoneCount}`,
      color: "#10131D",
    },
    {
      label: t("lbl_total_financial_milestones"),
      value: `${contract?.activeChildrenCount || milestones?.length || 0}`,
      color: "#00897B",
    },
  ];

  const { seller } = GET_MILESTONE_STATUSES(t);

  if (!contract) return <></>;

  return (
    <div className={styles.outerContainer}>
      <div
        className={classNames(
          styles.contractContainer,
          styles.paddedContainer
        )}>
        <BackButton onClick={back} />
        <div className={styles.headerRow}>
          <div className={styles.headerBlock}>
            <h1 className={styles.title}>{contract?.itemDetail?.title}</h1>
            <p className={styles.subtitle}>
              {contract?.itemDetail?.description}
            </p>
          </div>
        </div>

        <div className={styles.statsRow}>
          {complexStats.map((stat) => (
            <div key={stat.label} className={styles.statCard}>
              <p className={styles.statLabel}>{stat.label}</p>
              <h3 className={styles.statValue} style={{ color: stat.color }}>
                {stat.value}
              </h3>
            </div>
          ))}
        </div>

        <div className={styles.milestonesBlock}>
          <div className={styles.buttonGroup}>
            <h2>{t("lbl_transactions")}</h2>
          </div>
          <div className={styles.milestonesBlock}>
            {milestones?.map((milestone, index) => {
              const sumFor = (role: ESenderRole) =>
                milestone?.parties
                  ?.filter((party) => party?.role === role)
                  ?.reduce((acc, cur) => acc + +(cur?.amount || 0), 0) ?? 0;
              const milestoneAmount = Math.max(
                sumFor(ESenderRole.CUSTOMER),
                sumFor(ESenderRole.PROVIDER)
              );
              const formattedDeadline = milestone?.deadLine
                ? new Date(milestone.deadLine).toLocaleDateString("ar-EG", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : "";

              // A cash-in (CUSTOMER_PAY) that arrived via CONTRACT_TRANSFER was
              // funded by another linked contract rather than by a buyer
              // payment. Only the buyer-only refund copy the backend creates is
              // a back-to-source refund; a user-made destination milestone is
              // never buyerOnly.
              const cashInTransaction = milestone?.transactions?.find(
                (transaction) =>
                  transaction?.type === ETrasactionsType.CUSTOMER_PAY &&
                  transaction?.status === "SUCCESS"
              );
              const isBackToSourceRefund =
                !!milestone?.buyerOnly &&
                cashInTransaction?.paymentMethod ===
                  EPaymentBrand.CONTRACT_TRANSFER;
              const formattedRefundDate = cashInTransaction?.createdAt
                ? new Date(cashInTransaction.createdAt).toLocaleDateString(
                    "ar-EG",
                    { day: "numeric", month: "long", year: "numeric" }
                  )
                : formattedDeadline;

              return (
                <a
                  key={milestone?.id}
                  className={classNames(styles.milestoneRow, {
                    [styles.refundRow]: isBackToSourceRefund,
                  })}
                  href={`/contract/${milestone?.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/contract/${milestone?.id}`);
                    actions?.resetContractState();
                  }}
                  style={{ cursor: "pointer" }}>
                  {isBackToSourceRefund ? (
                    <span className={styles.refundIcon} aria-hidden="true">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M9 14L4 9l5-5" />
                        <path d="M4 9h10a6 6 0 0 1 6 6v1" />
                      </svg>
                    </span>
                  ) : (
                    <img src="/assets/images/svg/flag.svg" alt="flag" />
                  )}
                  <div className={styles.milestoneCol}>
                    <h4 className={styles.milestoneTitle}>
                      {isBackToSourceRefund
                        ? `${t("lbl_refund_from")} : ${
                            milestones?.[0]?.itemDetail?.title
                          }`
                        : `${t("lbl_milestone")} ${getArabicOrdinalFemale(
                            index + 1
                          )} : ${milestone?.itemDetail?.title}`}
                    </h4>
                    <p className={styles.milestoneMeta}>{`${t(
                      isBackToSourceRefund ? "lbl_refund_date" : "lbl_deadline"
                    )} : ${
                      isBackToSourceRefund
                        ? formattedRefundDate
                        : formattedDeadline
                    }`}</p>
                    <div className={styles.milestoneStats}>
                      <div>
                        <p className={styles.metaLabel}>
                          {t(
                            isBackToSourceRefund
                              ? "lbl_refund_value"
                              : "lbl_milestone_value"
                          )}
                        </p>
                        <h5 className={styles.metaValue}>
                          {`${milestoneAmount?.toLocaleString()} ${t(
                            "lbl_saudi_currency"
                          )}`}
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className={styles.milestoneSideCol}>
                    <div
                      className={classNames(styles.statusPill, {
                        [styles.statusActive]:
                          !isBackToSourceRefund &&
                          milestone?.status &&
                          milestone?.status !== ECONTRACT_STATUS.DRAFT,
                        [styles.refundBadge]: isBackToSourceRefund,
                      })}>
                      <p>
                        {isBackToSourceRefund
                          ? t("lbl_refunded_amount")
                          : milestone?.status
                          ? seller[milestone?.status]?.title
                          : t("lbl_draft")}
                      </p>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>

        {id && <SettlementRequests complexContractId={id} />}

        {!!contract?.itemDetail?.images?.length && (
          <div className={styles.attachments}>
            <h2>{t("lbl_attachments")}</h2>
            <div className={styles.attachmentsImages}>
              {contract.itemDetail.images.map((image) => (
                <img key={image} src={image} alt="image" />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MilestoneContractDetails;
